import { ReactNode } from 'react';

import get from 'lodash-es/get';

import { useRegisterFormNavigationField } from '@/hooks';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormLabelProps, IconButton, Tooltip, styled } from '@mui/joy';
import FormControl, { FormControlProps } from '@mui/joy/FormControl';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';

import { FormErrorMessage } from '@components/FormFields/FormErrorMessage';
import { FormLabel } from '@components/ui/FormField';
import { TextSkeleton } from '@components/ui/skeletons/TextSkeleton';

const CustomIconButton = styled(IconButton)({
  padding: 2,
  margin: 0,
  minWidth: 'auto',
  minHeight: 'auto',
});

export const CustomTooltip = ({
  tooltip,
  width = '245px',
  icon = (
    <InfoOutlinedIcon
      sx={{
        height: '16px',
        width: '16px',
        color: theme => theme.palette.neutral['500'],
      }}
    />
  ),
}: {
  tooltip: string;
  icon?: ReactNode;
  width?: string;
}) => {
  const tooltipTeax = (
    <Typography sx={{ width: { width }, color: 'neutral.100' }} level='body-sm'>
      {tooltip}
    </Typography>
  );
  return (
    <>
      <Tooltip arrow color='neutral' title={tooltipTeax} placement='bottom'>
        <CustomIconButton>{icon}</CustomIconButton>
      </Tooltip>
    </>
  );
};

export const FormFieldPreview = ({
  show = true,
  label,
  disabled,
  children,
  style,
  loading = false,
  tooltip,
  labelSx,
  displayFalsy = false,
}: Pick<FormFieldProps, 'label' | 'children' | 'disabled' | 'tooltip'> & {
  show?: boolean;
  loading?: boolean;
  style?: FormControlProps['style'];
  labelSx?: FormLabelProps['sx'];
  displayFalsy?: boolean;
}) => {
  const displayChildren =
    children !== undefined && children !== null
      ? children
      : displayFalsy
        ? children
        : '-';
  const content = loading ? '' : displayChildren;

  return show ? (
    <FormControl style={style}>
      <Stack direction='row' alignItems='center' gap={1}>
        {label && (
          <FormLabel preview disabled={disabled} sx={labelSx}>
            {label}
          </FormLabel>
        )}
        {tooltip && <CustomTooltip tooltip={tooltip} />}
      </Stack>
      <Typography
        component='div'
        level='title-sm'
        fontWeight='bold'
        textColor='neutral.700'
        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
      >
        <TextSkeleton loading={loading} sx={{}}>
          {content}
        </TextSkeleton>
      </Typography>
    </FormControl>
  ) : null;
};

export interface FormFieldProps {
  hidden?: boolean;
  control: any;
  name: string;
  errorName?: string;
  required?: boolean;
  preview?: boolean;
  label?: string | ReactNode | null;
  type?: string;
  placeholder?: string;
  value?: any;
  onChange?: any;
  onBlur?: any;
  component?: any;
  children?: any;
  errorMessage?: string;
  touchedName?: string;
  hideErrorMessage?: boolean;
  hideErrorMessageFn?: (errorMessage: string) => boolean;
  startDecorator?: ReactNode;
  endDecorator?: ReactNode;
  rightEl?: ReactNode;
  customPreviewRender?: boolean;
  renderPreview?: (props: { name: string; value: any }) => ReactNode;
  disabled?: boolean;
  tooltip?: string;
  loading?: boolean;
  hint?: string;
  sx?: FormControlProps['sx'];
  componentProps?: any;
  fieldRef?: React.RefObject<HTMLElement>;
}

export const FormField = ({
  hidden,
  control,
  preview,
  name,
  errorName,
  touchedName,
  label = null,
  required,
  component: Component,
  value,
  onChange,
  onBlur,
  children,
  rightEl,
  errorMessage,
  hideErrorMessage,
  hideErrorMessageFn,
  customPreviewRender,
  renderPreview,
  disabled,
  loading = false,
  sx,
  tooltip,
  hint,
  componentProps: additionalComponentProps,
  ...props
}: FormFieldProps) => {
  const { fieldRef } = useRegisterFormNavigationField(name, required);

  if (hidden || !control) {
    return null;
  }

  const { values, touched, handleChange, handleBlur, errors } = control;

  let isTouched = get(touched, touchedName || name);

  if (isTouched instanceof Array) {
    isTouched = true;
  } else if (typeof isTouched === 'object') {
    isTouched = Object.values(isTouched).some(v => v);
  }

  let isInvalid =
    !!errorMessage || (!!get(errors, errorName || name) && isTouched);

  if (hideErrorMessageFn) {
    isInvalid = hideErrorMessageFn(get(errors, errorName || name))
      ? false
      : isInvalid;
  }

  const componentProps = {
    ...props,
    name,
    value: value ?? get(values, name),
    onChange: onChange || handleChange,
    onBlur: onBlur || handleBlur,
    fieldRef,
  };

  if (preview) {
    // TODO remove customPreviewRender prop
    return (
      <FormFieldPreview
        label={label}
        loading={loading}
        disabled={disabled && !preview}
      >
        {renderPreview?.(componentProps) ||
          (customPreviewRender ? (
            typeof children === 'function' ? (
              children({ ...componentProps, isInvalid })
            ) : Component ? (
              <Component {...componentProps} />
            ) : null
          ) : (
            get(values, name, '-')
          ))}
      </FormFieldPreview>
    );
  }

  if (typeof children === 'function') {
    return (
      <FormControl error={isInvalid} sx={sx}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          {label && (
            <FormLabel required={required} disabled={disabled}>
              {label} {tooltip && <CustomTooltip tooltip={tooltip} />}
            </FormLabel>
          )}
          {rightEl}
        </Stack>
        {children({ ...componentProps, isInvalid, fieldRef })}
        {!!hint && (
          <Typography level='body-sm' textColor='neutral.600' sx={{ mt: 0.25 }}>
            {hint}
          </Typography>
        )}
        {!hideErrorMessage && isInvalid && (
          <FormErrorMessage>
            {errorMessage || get(errors, errorName || name)}
          </FormErrorMessage>
        )}
      </FormControl>
    );
  } else if (Component) {
    return (
      <FormControl error={isInvalid} sx={sx}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          {label && (
            <FormLabel required={required} disabled={disabled}>
              {label}
            </FormLabel>
          )}
          {rightEl}
        </Stack>
        <Component
          {...componentProps}
          {...additionalComponentProps}
          fieldRef={fieldRef}
        />
        {!hideErrorMessage && isInvalid && (
          <FormErrorMessage>{get(errors, errorName || name)}</FormErrorMessage>
        )}
      </FormControl>
    );
  } else {
    return null;
  }
};

export default FormField;
